import { useUserAuth } from 'contexts/UserAuth';
import { useRouter } from 'next/router';
import React from 'react';
import useSWR from 'swr';
import api, { swrFetcher } from 'utils/api';

const { createContext, useContext, useState, useCallback, useMemo } = React;

const CART_API = '/api/v3/me/groupBuyingCart';
const CART_EXPIRES_BUFFER = 20 * 60 * 1000; // 20 minutes in milliseconds
export const GroupBuyCartContext = createContext({});
GroupBuyCartContext.displayName = 'GroupBuyCart';

export const GroupBuyCartProvider = (props) => {
  const { children } = props;
  const { user, anonymousUser, getAuthorizationHeader, ensureAuthHeaders } =
    useUserAuth();

  const router = useRouter();
  const isGroupBuyPage = router.pathname.startsWith('/groupbuy');

  const { data: cartData, mutate: mutateCart } = useSWR(
    (user || anonymousUser) && isGroupBuyPage ? CART_API : null,
    (url) => swrFetcher(url, ensureAuthHeaders)
  );

  const { data: pendingRedeem, mutate: mutatePendingRedeem } = useSWR(
    (user || anonymousUser) && isGroupBuyPage
      ? '/api/v3/me/pendingRedeems'
      : null,
    (url) => swrFetcher(url, ensureAuthHeaders),
    { fallbackData: [] }
  );

  const [showCartModal, setShowCartModal] = useState(false);
  const [cartTimer, setCartTimer] = useState(null);

  const cartItems = useMemo(() => {
    if (!cartData) {
      setCartTimer(null);
      return [];
    }
    const items = Array.isArray(cartData) ? cartData : cartDafta.items || [];
    const latestUpdate = items.reduce((acc, item) => {
      const itemTime = new Date(item.updated_at).getTime();
      if (!acc || itemTime > acc) {
        return itemTime;
      }
    }, null);
    setCartTimer(latestUpdate + CART_EXPIRES_BUFFER);
    return items.map((item) => {
      const gb = item.group_buying || {};
      return {
        id: gb.id,
        name: gb.name,
        en_name: gb.en_name,
        brand: gb.brand,
        price: gb.price,
        original_price: gb.original_price,
        images: gb.images,
        tags: gb.tags,
        quota: gb.quota,
        product: gb.product,
        quantity: item.count,
        rest_quota: gb.rest_quota,
        show_low_stock: gb.show_low_stock,
      };
    });
  }, [cartData]);

  const cartExpiresAt = cartItems.length > 0 ? cartTimer : null;

  const pendingRedeemItems = useMemo(() => {
    if (!pendingRedeem) return [];
    return pendingRedeem.filter((redeem) => {
      return redeem.type === 'GROUP_BUYING';
    });
  }, [pendingRedeem]);

  const addToCart = useCallback(
    async (product, quantity) => {
      try {
        const headers = await ensureAuthHeaders();
        const existing = cartItems.find((item) => item.id === product.id);
        const newCount = (existing?.quantity || 0) + quantity;
        await api.request({
          method: 'PUT',
          url: CART_API,
          data: { id: product.id, count: newCount },
          headers,
        });
        await mutateCart();
      } catch (err) {
        console.error('addToCart error:', err);
      }
    },
    [getAuthorizationHeader, mutateCart, cartItems]
  );

  const removeFromCart = useCallback(
    async (productId) => {
      try {
        const headers = await ensureAuthHeaders();
        await api.request({
          method: 'PUT',
          url: CART_API,
          data: { id: productId, count: 0 },
          headers,
        });
        await mutateCart();
      } catch (err) {
        console.error('removeFromCart error:', err);
      }
    },
    [getAuthorizationHeader, mutateCart]
  );

  const updateQuantity = useCallback(
    async (productId, newQty) => {
      if (newQty < 1) {
        removeFromCart(productId);
        return;
      }
      try {
        const headers = await ensureAuthHeaders();
        await api.request({
          method: 'PUT',
          url: CART_API,
          data: { id: productId, count: newQty },
          headers,
        });
        await mutateCart();
      } catch (err) {
        console.error('updateQuantity error:', err);
      }
    },
    [getAuthorizationHeader, mutateCart, removeFromCart]
  );

  const clearCart = useCallback(async () => {
    try {
      const headers = await ensureAuthHeaders();

      await api.request({
        method: 'PUT',
        url: CART_API,
        headers,
      });
      await mutateCart();
      setCartTimer(null);
    } catch (err) {
      console.error('clearCart error:', err);
    }
  }, [getAuthorizationHeader, mutateCart]);

  const cartCount = useMemo(
    () => cartItems.reduce((sum, item) => sum + item.quantity, 0),
    [cartItems]
  );

  const cartTotal = useMemo(
    () => cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0),
    [cartItems]
  );

  const cartSavings = useMemo(
    () =>
      cartItems.reduce((sum, item) => {
        const saving = (item.original_price - item.price) * item.quantity;
        return sum + (saving > 0 ? saving : 0);
      }, 0),
    [cartItems]
  );

  return (
    <GroupBuyCartContext.Provider
      value={{
        cartItems,
        showCartModal,
        setShowCartModal,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        cartCount,
        cartTotal,
        cartSavings,
        cartExpiresAt,
        mutateCart,
        pendingRedeemItems,
        mutatePendingRedeem,
      }}
    >
      {children}
    </GroupBuyCartContext.Provider>
  );
};

export const useGroupBuyCart = () => {
  return useContext(GroupBuyCartContext);
};
